import { codeProductData } from '@/features/Product/stores/rawData/codeProductData';
import type { ProductTag } from '@/features/Product/types/models';
import { pickWeighted } from '@/composables/pickWeighted';
import { useProductBaseStore } from '@/features/Product/stores/productBase';

const RANDOMISATION_TAG_PREFIX = 'a-or-b-';
const LETTER_VARIANT_SUFFIX = /-[a-z]$/i;
const PRODUCT_PATH_PATTERN = /^\/products\/([^/]+)(\/.*)?$/;
const products = [...codeProductData.values()];

const findRandomisationTag = (handle: string): ProductTag | undefined => {
  const currentProduct = products.find((product) => product.attributes.productHandle === handle);
  return currentProduct?.attributes.productTags?.find((tag) =>
    tag.startsWith(RANDOMISATION_TAG_PREFIX),
  );
};

const findRandomisationGroupHandles = (randomisationTag: ProductTag): string[] =>
  products
    .filter((product) => product.attributes.productTags?.includes(randomisationTag))
    .map((product) => product.attributes.productHandle)
    .filter((productHandle): productHandle is string => Boolean(productHandle));

export const resolveBaseProductHandle = (handle: string): string => {
  const randomisationTag = findRandomisationTag(handle);
  if (!randomisationTag) return handle;
  return (
    findRandomisationGroupHandles(randomisationTag).find(
      (productHandle) => !LETTER_VARIANT_SUFFIX.test(productHandle),
    ) ?? handle
  );
};

export const resolveBaseProductPath = (path: string): string => {
  const match = path.match(PRODUCT_PATH_PATTERN);
  if (!match) return path;
  const [, handle, rest = ''] = match;
  return `/products/${resolveBaseProductHandle(handle)}${rest}`;
};

export const resolveRandomisedProductHandle = (handle: string): string => {
  const randomisationTag = findRandomisationTag(handle);
  if (!randomisationTag) return handle;
  const { findProductByHandle } = useProductBaseStore();
  const options = findRandomisationGroupHandles(randomisationTag)
    .filter((productHandle) => Boolean(findProductByHandle(productHandle)))
    .map((productHandle) => ({
      productHandle,
      weight: 1,
    }));
  if (options.length < 2) return handle;

  return pickWeighted(options)?.productHandle ?? handle;
};
